/**
 * Pure decision logic for the web's pre-translation "prefetch" ping — see
 * `prefetchViaGateway` in `@/lib/translateGateway/client.ts` for the request
 * itself and the server contract it targets. Kept out of `Translator.tsx` so
 * the paste/pause/skip/dedupe rules are unit-testable without mounting the
 * component.
 */
import type { GenderId, LangCode, ModeId, ModelId } from '@/lib/baba-api'

/**
 * The one place `inputText` gets normalized into what actually reaches the
 * gateway as `text`. The server keys its prefetch cache on the exact prompt,
 * so a prefetch and the real translation that follows it MUST reduce
 * `inputText` the same way — `Translator.tsx`'s `runTranslate` and its
 * prefetch effect both call this instead of each having their own
 * `inputText.trim()`, so they can't quietly drift onto two different
 * strings (leading/trailing whitespace today; wherever this needs to grow).
 */
export function normalizeTranslateText(text: string): string {
  return text.trim()
}

/** 800ms of typing silence reads as "done for now, might translate soon" —
 *  long enough that a fast typist doesn't fire a request per keystroke. */
export const PREFETCH_PAUSE_DELAY_MS = 800

/** A single text change that grows the input by at least this many
 *  characters reads as a paste even when it didn't arrive through an
 *  explicit paste event (drag-drop text, IME composition, a programmatic
 *  fill from an example chip). */
const PASTE_GROWTH_THRESHOLD = 8

export type PrefetchTrigger = 'paste' | 'pause'

/**
 * Classifies one `inputText` change. `composing` is true only when the
 * change itself was the user typing a keystroke or pasting — false for
 * every OTHER way `inputText` can change (opening a recent/history item,
 * the language-swap that moves the output into the input, clearing, an
 * example-chip fill, a landing-page handoff, …). None of those are the user
 * composing something new, so they never classify as anything, regardless
 * of size. `explicitPaste` is true when a composing change was caused by a
 * real paste (the window paste listener, or the clipboard "Paste" button in
 * `Translator.tsx`) — the growth heuristic below is only the fallback for a
 * paste that arrives some other way. Returns `null` when the resulting text
 * is too short to be worth prefetching at all.
 */
export function classifyTextChange(params: {
  previousText: string
  nextText: string
  explicitPaste: boolean
  composing: boolean
}): PrefetchTrigger | null {
  if (!params.composing) return null
  if (params.nextText.trim().length < 2) return null
  const grew = params.nextText.length - params.previousText.length
  if (params.explicitPaste || grew >= PASTE_GROWTH_THRESHOLD) return 'paste'
  return 'pause'
}

export interface PrefetchSkipParams {
  trimmedTextLength: number
  charLimit: number
  tab: string
  mode: ModeId | string
  isTranslating: boolean
}

/** The gate every trigger passes through before a request goes out — mirrors
 *  the real translation's own gates (char limit, tab, mode, in-flight) so a
 *  prefetch never fires for a request `runTranslate` itself would refuse. */
export function shouldSkipPrefetch(p: PrefetchSkipParams): boolean {
  return (
    p.trimmedTextLength < 2 ||
    p.trimmedTextLength > p.charLimit ||
    p.tab !== 'text' ||
    p.mode === 'summary' ||
    p.isTranslating
  )
}

export interface PrefetchSignatureInput {
  text: string
  sourceLang: LangCode | string
  targetLang: LangCode | string
  gender?: GenderId | string
  genderContext?: string
  mode?: ModeId | string
  model?: ModelId | string
  style?: string
}

/**
 * A signature of exactly the fields that shape the gateway prompt. Comparing
 * a fresh signature against the last one actually sent is how "never send
 * the same body twice in a row" and "reset when languages/gender/mode
 * change" both fall out of the same check — a picker change produces a new
 * signature, so it is never mistaken for a repeat.
 */
export function prefetchSignature(p: PrefetchSignatureInput): string {
  return JSON.stringify([
    p.text,
    p.sourceLang,
    p.targetLang,
    p.gender ?? '',
    p.genderContext ?? '',
    p.mode ?? '',
    p.model ?? '',
    p.style ?? '',
  ])
}
